import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../utils/api';
import { ChevronDown, ChevronRight, ChevronLeft, Trophy, Flame, Footprints, TreePine, Droplets, Car, Award, Users, TrendingUp, Target, Zap, Star, Gift, BarChart3, Calendar, Clock, Hexagon, Medal, Crown, Gem, ShieldCheck, FileText, Activity, Heart, MessageSquare, CheckCircle } from 'lucide-react';
import useScrollReveal from '../hooks/useScrollReveal';
import OrgLeaderboard from '../components/shared/OrgLeaderboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ImpactPage = ({ TopHeader, BottomNav, useAuth }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [scoreData, setScoreData] = useState(null);
  const [impactTotals, setImpactTotals] = useState([]);
  const [scoreBreakdown, setScoreBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBadgesModal, setShowBadgesModal] = useState(false);
  
  // UI states - check URL param for auto-expand
  const shouldShowLevels = searchParams.get('showLevels') === 'true';
  const [showImpactLevels, setShowImpactLevels] = useState(shouldShowLevels);
  const [impactCarouselIndex, setImpactCarouselIndex] = useState(0);
  const [showAllAchievements, setShowAllAchievements] = useState(false);
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [weekStreakData, setWeekStreakData] = useState(null);
  const [showScoreBreakdown, setShowScoreBreakdown] = useState(false);

  const revealRef = useScrollReveal([loading]);

  // Theme colors
  const TEAL_ACCENT = '#5DBFAD';
  const TEAL_LIGHT = '#E8F5F3';
  const ORANGE_ACCENT = '#F8AD7B';
  const ORANGE_LIGHT = '#FEF3E8';
  const MISSED_COLOR = '#EF4444'; // Red for missed days

  // Badge icon mapping - professional flat icons with theme colors
  const getBadgeIcon = (badgeType, size = 24) => {
    const iconProps = { size, strokeWidth: 1.5 };
    const badgeIcons = {
      'first_action': { icon: Star, color: '#F5A623', bg: 'rgba(245, 166, 35, 0.12)' },
      'streak_7': { icon: Flame, color: '#E8795E', bg: 'rgba(232, 121, 94, 0.12)' },
      'streak_30': { icon: Flame, color: '#9D5CFF', bg: 'rgba(157, 92, 255, 0.12)' },
      'helper': { icon: Users, color: TEAL_ACCENT, bg: TEAL_LIGHT },
      'leader': { icon: Crown, color: '#F5A623', bg: 'rgba(245, 166, 35, 0.12)' },
      'impact_bronze': { icon: Medal, color: '#CD7F32', bg: 'rgba(205, 127, 50, 0.12)' },
      'impact_silver': { icon: Medal, color: '#C0C0C0', bg: 'rgba(192, 192, 192, 0.15)' },
      'impact_gold': { icon: Medal, color: '#FFD700', bg: 'rgba(255, 215, 0, 0.12)' },
      'impact_platinum': { icon: Gem, color: '#9D5CFF', bg: 'rgba(157, 92, 255, 0.12)' },
      'champion': { icon: Trophy, color: '#F5A623', bg: 'rgba(245, 166, 35, 0.12)' },
      'verified': { icon: ShieldCheck, color: TEAL_ACCENT, bg: TEAL_LIGHT },
      'default': { icon: Award, color: TEAL_ACCENT, bg: TEAL_LIGHT }
    };
    
    const badge = badgeIcons[badgeType] || badgeIcons['default'];
    const IconComponent = badge.icon;
    
    return (
      <div style={{
        width: size + 16,
        height: size + 16,
        borderRadius: '12px',
        background: badge.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <IconComponent {...iconProps} color={badge.color} />
      </div>
    );
  };

  // Fetch week streak data on mount AND whenever the modal opens so the
  // "Streak" tile in Your Stats stays in sync with the modal — both read
  // from /streak/week (the recompute-on-read endpoint).
  useEffect(() => {
    fetchWeekStreakData();
  }, []);
  useEffect(() => {
    if (showStreakModal) {
      fetchWeekStreakData();
    }
  }, [showStreakModal]);

  const fetchWeekStreakData = async () => {
    try {
      const response = await axios.get(`${API}/streak/week`);
      setWeekStreakData(response.data);
    } catch (error) {
      console.error('Failed to fetch streak data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [scoreRes, impactRes, breakdownRes] = await Promise.all([
        axios.get(`${API}/social-score/me`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        // Real-World Impact carousel now sources every tile from the
        // metric-driven totals endpoint. Whatever `metric_definitions`
        // knows about is what shows up here — no hardcoded metric names,
        // no fallback labels. `user_id` is the current authenticated user
        // (they view their own impact page); org scoping comes from the
        // token on the backend, not the query string.
        axios.get(`${API}/impact/totals?user_id=${encodeURIComponent(user?.id || '')}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/social-score/breakdown`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setScoreData(scoreRes.data);
      setImpactTotals(Array.isArray(impactRes.data) ? impactRes.data : []);
      setScoreBreakdown(breakdownRes.data);
    } catch (error) {
      console.error('Failed to fetch impact data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImpactBadgeIcon = (level) => {
    const badgeConfig = {
      'Bronze': { icon: Medal, color: '#CD7F32', bg: 'rgba(205, 127, 50, 0.12)' },
      'Silver': { icon: Medal, color: '#A8A8A8', bg: 'rgba(168, 168, 168, 0.12)' },
      'Gold': { icon: Medal, color: '#D4AF37', bg: 'rgba(212, 175, 55, 0.12)' },
      'Platinum': { icon: Gem, color: '#9D5CFF', bg: 'rgba(157, 92, 255, 0.12)' },
      'Hero': { icon: Trophy, color: '#F5A623', bg: 'rgba(245, 166, 35, 0.12)' }
    };
    const config = badgeConfig[level] || { icon: Star, color: TEAL_ACCENT, bg: TEAL_LIGHT };
    const IconComponent = config.icon;
    
    return (
      <div style={{
        width: 28,
        height: 28,
        borderRadius: '8px',
        background: config.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <IconComponent size={16} color={config.color} strokeWidth={1.5} />
      </div>
    );
  };

  // Real-World Impact tiles are derived 1:1 from `GET /api/impact/totals`.
  // Every metric returned by the endpoint becomes one card. Labels, units,
  // and values come from the API — nothing is hardcoded here (no metric
  // name mapping, no fallback numbers). A small color palette cycles by
  // index so cards stay visually distinguishable without tying colors to
  // specific metrics.
  const TILE_PALETTE = ['#10B981', '#F59E0B', '#3B82F6', '#22C55E', '#8B5CF6', '#EC4899', '#06B6D4', '#EF4444'];

  const formatTileValue = (v) => {
    if (v === null || v === undefined) return '0';
    const n = Number(v);
    if (!Number.isFinite(n)) return '0';
    // Integers render with thousands separators. Fractional values (e.g.
    // 0.0463 kg CO2) are kept readable at 2 decimals; we strip trailing
    // zeros so "5.50" becomes "5.5" for cleaner display.
    if (Number.isInteger(n)) return n.toLocaleString();
    const rounded = Math.round(n * 100) / 100;
    if (Number.isInteger(rounded)) return rounded.toLocaleString();
    return rounded.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const impactTiles = (impactTotals || []).map((item, index) => ({
    icon: BarChart3,
    value: formatTileValue(item.value),
    label: item.label,
    description: item.unit,
    borderColor: TILE_PALETTE[index % TILE_PALETTE.length],
  }));

  // Impact levels data with point ranges
  const impactLevels = [
  { level: 'Bronze', icon: <Award size={18} color="#CD7F32" />, description: 'Getting started', range: '0 - 499' },
  { level: 'Silver', icon: <Star size={18} color="#A8A9AD" />, description: 'Building momentum', range: '500 - 1,499' },
  { level: 'Gold', icon: <Star size={18} color="#F28C28" fill="#F28C28" />, description: 'Consistent contributor', range: '1,500 - 4,999' },
  { level: 'Platinum', icon: <Gem size={18} color="#4DB6AC" />, description: 'Impact leader', range: '5,000 - 9,999' },
  { level: 'Hero', icon: <Trophy size={18} color="#E76F51" />, description: 'Top performer', range: '10,000+' },
];

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="page-container" ref={revealRef}>
      <TopHeader title="Your Impact" showBack={false} />
      <div className="content-wrapper" style={{ background: '#F8FAFB' }}>
        <div className="content-section reveal-stagger" style={{ padding: '0 16px' }}>
          
          {/* ═══ IMPACT LEVEL SECTION ═══ */}
          <div className="reveal" style={{
            position: 'relative', overflow: 'hidden', borderRadius: 24,
            background: 'rgba(77, 182, 172, 0.06)', padding: 12, marginBottom: 16,
          }}>
            <div style={{ position: 'absolute', top: -60, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(77, 182, 172, 0.14)', animation: 'blobBreathe 9s ease-in-out infinite', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -40, left: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(76, 175, 142, 0.10)', animation: 'blobBreatheSlow 10s ease-in-out infinite', pointerEvents: 'none' }} />

            {/* Impact Level Card - Clickable */}
            <div 
              onClick={() => setShowImpactLevels(!showImpactLevels)}
              className="glow-card-impact glow-teal-i"
              style={{
                position: 'relative', zIndex: 1,
                background: '#FFFFFF',
                borderRadius: 16,
                padding: '20px 24px',
                cursor: 'pointer',
              }}
            >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ 
                  width: 56, 
                  height: 56, 
                  borderRadius: 12,
                  background: TEAL_LIGHT,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28
                }}>
                  {getImpactBadgeIcon(scoreData.impact_level)}
                </div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#1F2937' }}>
                    {scoreData.impact_level}
                  </div>
                  <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                    Tap to view all levels
                  </div>
                </div>
              </div>
              <ChevronDown 
                size={24} 
                color={TEAL_ACCENT}
                style={{ 
                  transform: showImpactLevels ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              />
            </div>
            
            {/* Expandable Impact Levels */}
            {showImpactLevels && (
              <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 12 }}>
                  Impact Levels
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {impactLevels.map((item) => (
                    <div 
                      key={item.level}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: scoreData.impact_level === item.level ? TEAL_LIGHT : '#F9FAFB',
                        border: scoreData.impact_level === item.level ? `1px solid ${TEAL_ACCENT}` : '1px solid transparent'
                      }}
                    >
                      <span style={{ fontSize: 20 }}>{item.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          marginBottom: 2
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                              {item.level}
                            </span>
                            {scoreData.impact_level === item.level && (
                              <span style={{ fontSize: 11, color: TEAL_ACCENT, fontWeight: 500 }}>
                                Your Level
                              </span>
                            )}
                          </div>
                          <span style={{ 
                            fontSize: 12, 
                            fontWeight: 500, 
                            color: scoreData.impact_level === item.level ? TEAL_ACCENT : '#9CA3AF'
                          }}>
                            {item.range} pts
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: '#6B7280' }}>{item.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          </div>

          {/* ═══ SCORE BREAKDOWN SECTION ═══ */}
          {scoreBreakdown && (
            <div className="reveal" style={{
              position: 'relative', overflow: 'hidden', borderRadius: 22,
              background: 'rgba(76, 175, 142, 0.05)', padding: 10, marginBottom: 16,
            }}>
              <div style={{ position: 'absolute', top: -50, left: -40, width: 190, height: 190, borderRadius: '50%', background: 'rgba(76, 175, 142, 0.12)', animation: 'blobBreathe 10s ease-in-out infinite', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', bottom: -30, right: -20, width: 120, height: 120, borderRadius: '50%', background: 'rgba(242, 140, 40, 0.10)', animation: 'blobBreatheSlow 9s ease-in-out infinite', pointerEvents: 'none' }} />

              <div style={{ position: 'relative', zIndex: 1 }}>
              {/* Toggle Header */}
              <button
                onClick={() => setShowScoreBreakdown(!showScoreBreakdown)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px 20px',
                  background: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: showScoreBreakdown ? '16px 16px 0 0' : '16px',
                  cursor: 'pointer'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: TEAL_LIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <BarChart3 size={20} color={TEAL_ACCENT} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#1F2937', textAlign: 'left' }}>
                      Score Breakdown
                    </div>
                    <div style={{ fontSize: 13, color: '#6B7280', textAlign: 'left' }}>
                      Current: <span style={{ fontWeight: 600, color: TEAL_ACCENT }}>{scoreData?.social_score?.toLocaleString() || 0}</span> points
                    </div>
                  </div>
                </div>
                <ChevronDown 
                  size={20} 
                  color="#9CA3AF" 
                  style={{ 
                    transform: showScoreBreakdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </button>

              {/* Collapsible Content */}
              {showScoreBreakdown && (
                <div style={{
                  background: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderTop: 'none',
                  borderRadius: '0 0 16px 16px',
                  overflow: 'hidden'
                }}>
                  {(() => {
                    const totalScore = scoreData?.social_score || 1;

                    // Field names must match the actual shape returned by
                    // GET /api/social-score/breakdown — see routes/social.py.
                    // Response shape:
                    //   individual_activities: { count, score, activities }
                    //   team_activities:       { count, score, activities }
                    //   company_activities:    { count, score, activities }
                    //   achievements:          { count, score, items }
                    // Previous code read `.activities_score`, `.team_score`,
                    // `.challenges_score`, `.achievement_score` etc — none of
                    // those keys are ever produced, so every card rendered 0.
                    const individualScore = scoreBreakdown?.individual_activities?.score || 0;
                    const individualCount = scoreBreakdown?.individual_activities?.count || 0;

                    const teamScore = scoreBreakdown?.team_activities?.score || 0;
                    const teamCount = scoreBreakdown?.team_activities?.count || 0;

                    const companyScore = scoreBreakdown?.company_activities?.score || 0;
                    const companyCount = scoreBreakdown?.company_activities?.count || 0;

                    const communityScore = scoreBreakdown?.community_activities?.score || 0;
                    const communityCount = scoreBreakdown?.community_activities?.count || 0;

                    const achievementScore = scoreBreakdown?.achievements?.score || 0;
                    const achievementCount = scoreBreakdown?.achievements?.count || 0;
                    
                    const breakdownItems = [
                      { 
                        label: 'Individual Activities', 
                        description: 'Solo activities completed',
                        count: individualCount,
                        value: individualScore, 
                        color: TEAL_ACCENT,
                        bgColor: TEAL_LIGHT
                      },
                      { 
                        label: 'Team Activities', 
                        description: 'Activities done with team members',
                        count: teamCount,
                        value: teamScore, 
                        color: '#8B5CF6',
                        bgColor: 'rgba(139, 92, 246, 0.1)',
                        boost: '3x',
                        boostColor: '#8B5CF6'
                      },
                      { 
                        label: 'Company Activities', 
                        description: 'Company-wide initiatives',
                        count: companyCount,
                        value: companyScore, 
                        color: '#F59E0B',
                        bgColor: 'rgba(245, 158, 11, 0.1)',
                        boost: '3x',
                        boostColor: '#F59E0B'
                      },
                      {
                        label: 'Community',
                        description: 'Cross-organization community actions',
                        count: communityCount,
                        value: communityScore,
                        color: '#EC4899',
                        bgColor: 'rgba(236, 72, 153, 0.1)',
                        boost: '5x',
                        boostColor: '#EC4899'
                      },
                      { 
                        label: 'Achievements', 
                        description: 'Badges and milestones earned',
                        count: achievementCount,
                        value: achievementScore, 
                        color: '#EF4444',
                        bgColor: 'rgba(239, 68, 68, 0.1)'
                      }
                    ];

                    return breakdownItems.map((item, index) => {
                      const percentage = totalScore > 0 ? Math.round((item.value / totalScore) * 100) : 0;
                      return (
                        <div 
                          key={index}
                          style={{
                            padding: '16px 20px',
                            borderBottom: index < breakdownItems.length - 1 ? '1px solid #F3F4F6' : 'none'
                          }}
                        >
                          {/* Header Row */}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            marginBottom: 6
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                                {item.label}
                              </span>
                              <span style={{ 
                                fontSize: 11, 
                                fontWeight: 500,
                                color: '#6B7280',
                                background: '#F3F4F6',
                                padding: '2px 8px',
                                borderRadius: 10
                              }}>
                                {item.count}
                              </span>
                              {item.boost && (
                                <span style={{ 
                                  fontSize: 10, 
                                  fontWeight: 700,
                                  color: '#FFFFFF',
                                  background: item.boostColor,
                                  padding: '3px 8px',
                                  borderRadius: 10
                                }}>
                                  {item.boost} boost
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Description */}
                          <div style={{ 
                            fontSize: 12, 
                            color: '#9CA3AF',
                            marginBottom: 10
                          }}>
                            {item.description}
                          </div>
                          
                          {/* Progress Bar with Value */}
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 12
                          }}>
                            <div style={{
                              flex: 1,
                              height: 8,
                              background: '#F3F4F6',
                              borderRadius: 4,
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                height: '100%',
                                width: `${Math.min(percentage, 100)}%`,
                                background: item.color,
                                borderRadius: 4,
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'baseline',
                              gap: 4,
                              minWidth: 80,
                              justifyContent: 'flex-end'
                            }}>
                              <span style={{ 
                                fontSize: 16, 
                                fontWeight: 700, 
                                color: item.color 
                              }}>
                                {item.value.toLocaleString()}
                              </span>
                              <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                                ({percentage}%)
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
            </div>
          )}

          {/* ═══ REAL-WORLD IMPACT SECTION ═══ */}
          <div className="reveal" style={{
            position: 'relative', overflow: 'hidden', borderRadius: 22,
            background: 'rgba(242, 140, 40, 0.06)', padding: 12, marginBottom: 16,
          }}>
            <div style={{ position: 'absolute', top: -60, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(242, 140, 40, 0.13)', animation: 'blobBreathe 8s ease-in-out infinite', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -40, left: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(77, 182, 172, 0.10)', animation: 'blobBreatheSlow 10s ease-in-out infinite', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', marginBottom: 12 }}>
              Real-World Impact
            </h3>
            
            {impactTiles.length > 0 && (() => {
              // Clamp the carousel index against the current tile count. The
              // totals array can grow or shrink between fetches (e.g. a new
              // metric gets wired into an org, or one gets deactivated) and
              // the raw `impactCarouselIndex` state might otherwise point
              // past the end of the array on the next render.
              const safeIdx = Math.max(0, Math.min(impactCarouselIndex, impactTiles.length - 1));
              const tile = impactTiles[safeIdx];
              return (
              <div style={{ position: 'relative' }}>
                {/* Carousel Card with Thick Colored Border */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: 16,
                  padding: 24,
                  border: `3px solid ${tile.borderColor}`,
                  minHeight: 160,
                  transition: 'border-color 0.3s ease'
                }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    {/* Outlined Icon with matching border color */}
                    <div style={{
                      width: 64,
                      height: 64,
                      borderRadius: 16,
                      border: `2px solid ${tile.borderColor}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: 16
                    }}>
                      {React.createElement(tile.icon, {
                        size: 28,
                        color: tile.borderColor,
                        strokeWidth: 1.5
                      })}
                    </div>
                    
                    <div style={{ fontSize: 32, fontWeight: 700, color: '#1F2937', marginBottom: 4 }}>
                      {tile.value}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                      {tile.label}
                    </div>
                    <div style={{ fontSize: 13, color: '#6B7280' }}>
                      {tile.description}
                    </div>
                  </div>
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={() => setImpactCarouselIndex(prev => prev === 0 ? impactTiles.length - 1 : prev - 1)}
                  style={{
                    position: 'absolute', left: -12, top: '50%', transform: 'translateY(-50%)',
                    width: 36, height: 36, borderRadius: '50%', background: '#FFFFFF',
                    border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                >
                  <ChevronLeft size={20} color="#6B7280" />
                </button>
                
                <button
                  onClick={() => setImpactCarouselIndex(prev => prev === impactTiles.length - 1 ? 0 : prev + 1)}
                  style={{
                    position: 'absolute', right: -12, top: '50%', transform: 'translateY(-50%)',
                    width: 36, height: 36, borderRadius: '50%', background: '#FFFFFF',
                    border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                >
                  <ChevronRight size={20} color="#6B7280" />
                </button>

                {/* Dots Indicator */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 12 }}>
                  {impactTiles.map((t, index) => (
                    <button
                      key={index}
                      onClick={() => setImpactCarouselIndex(index)}
                      style={{
                        width: index === safeIdx ? 20 : 8,
                        height: 8, borderRadius: 4,
                        background: index === safeIdx ? t.borderColor : '#D1D5DB',
                        border: 'none', cursor: 'pointer', transition: 'all 0.2s ease'
                      }}
                    />
                  ))}
                </div>
              </div>
              );
            })()}
            </div>
          </div>

          {/* ═══ YOUR STATS SECTION ═══ */}
          <div className="reveal" style={{
            position: 'relative', overflow: 'hidden', borderRadius: 22,
            background: 'rgba(231, 111, 81, 0.05)', padding: 12, marginBottom: 16,
          }}>
            <div style={{ position: 'absolute', bottom: -50, right: -40, width: 190, height: 190, borderRadius: '50%', background: 'rgba(231, 111, 81, 0.12)', animation: 'blobBreathe 9s ease-in-out infinite', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', top: -30, left: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(76, 175, 142, 0.10)', animation: 'blobBreatheSlow 10s ease-in-out infinite', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', marginBottom: 12 }}>Your Stats</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {[
                { 
                  icon: Hexagon, 
                  value: scoreData.active_mad_count || 0, 
                  label: 'My Action', 
                  clickable: true, 
                  onClick: () => navigate('/profile?tab=mymad')
                },
                { 
                  icon: Flame, 
                  // Source from /streak/week (same endpoint the modal uses)
                  // so the Your Stats tile and the modal always show the
                  // identical number. Fall back to the cached user value
                  // only on first render before fetch completes.
                  value: `${weekStreakData?.current_streak ?? user.streak_count ?? 0}`, 
                  label: 'Streak', 
                  clickable: true, 
                  onClick: () => setShowStreakModal(true) 
                },
                { 
                  icon: Award, 
                  value: scoreData.achievements.length, 
                  label: 'Badges', 
                  clickable: true, 
                  onClick: () => setShowBadgesModal(true) 
                }
              ].map((stat, index) => (
                <div 
                  key={index} 
                  onClick={stat.clickable ? stat.onClick : undefined}
                  className="glow-card-impact glow-red-orange-i"
                  style={{
                    background: '#FFFFFF', borderRadius: 12, padding: '16px 8px',
                    textAlign: 'center',
                    cursor: stat.clickable ? 'pointer' : 'default',
                  }}
                >
                  <stat.icon size={20} color={TEAL_ACCENT} strokeWidth={1.5} style={{ marginBottom: 8 }} />
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>{stat.value}</div>
                  <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>{stat.label}</div>
                </div>
              ))}
            </div>
            </div>
          </div>

          {/* Badges Modal */}
          {showBadgesModal && (
            <div 
              onClick={() => setShowBadgesModal(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: 20
              }}
            >
              <div 
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: '#FFFFFF',
                  borderRadius: 16,
                  padding: 24,
                  width: '100%',
                  maxWidth: 340,
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  animation: 'fadeInUp 0.3s ease'
                }}
              >
                {/* Close button */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1F2937', margin: 0 }}>Badges Earned</h3>
                  <button
                    onClick={() => setShowBadgesModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 4,
                      color: '#9CA3AF'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Badges List */}
                {scoreData.achievements.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {scoreData.achievements.map((achievement) => (
                      <div key={achievement.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: 12,
                        background: '#F8FAFB',
                        borderRadius: 12,
                        border: '1px solid #E5E7EB'
                      }}>
                        {getBadgeIcon(achievement.badge_type || 'default', 24)}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                            {achievement.title}
                          </div>
                          {achievement.description && (
                            <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                              {achievement.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '32px 16px', color: '#6B7280' }}>
                    <Award size={40} color="#E5E7EB" style={{ marginBottom: 12 }} />
                    <div style={{ fontSize: 14 }}>No badges earned yet</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Complete actions to earn badges!</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Streak Modal */}
          {showStreakModal && (
            <div 
              onClick={() => setShowStreakModal(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: 20
              }}
            >
              <div 
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: '#FFFFFF',
                  borderRadius: 16,
                  padding: 24,
                  width: '100%',
                  maxWidth: 340,
                  animation: 'fadeInUp 0.3s ease'
                }}
              >
                {/* Close button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <button
                    onClick={() => setShowStreakModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 4,
                      color: '#9CA3AF'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Streak Badge */}
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${TEAL_LIGHT} 0%, rgba(93, 191, 173, 0.2) 100%)`,
                    border: `2px solid ${TEAL_ACCENT}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}>
                    <Flame size={32} color={TEAL_ACCENT} strokeWidth={1.5} />
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#1F2937', marginBottom: 4 }}>
                    {weekStreakData?.current_streak || user.streak_count} Day Streak!
                  </div>
                  <div style={{ fontSize: 14, color: '#6B7280' }}>
                    {(weekStreakData?.current_streak || user.streak_count) > 0 
                      ? "You're on the right track" 
                      : "Start logging to build your streak"}
                  </div>
                </div>

                {/* Week Progress */}
                <div style={{
                  background: '#F9FAFB',
                  borderRadius: 12,
                  padding: 16,
                  marginBottom: 20
                }}>
                  <div style={{ 
                    fontSize: 12, 
                    fontWeight: 600, 
                    color: '#6B7280', 
                    marginBottom: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    This Week
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    {(weekStreakData?.week_data || [
                      { day: 'Mon', isToday: false, isFuture: false, isCompleted: false, isMissed: false },
                      { day: 'Tue', isToday: false, isFuture: false, isCompleted: false, isMissed: false },
                      { day: 'Wed', isToday: false, isFuture: false, isCompleted: false, isMissed: false },
                      { day: 'Thu', isToday: false, isFuture: false, isCompleted: false, isMissed: false },
                      { day: 'Fri', isToday: false, isFuture: false, isCompleted: false, isMissed: false },
                      { day: 'Sat', isToday: false, isFuture: false, isCompleted: false, isMissed: false },
                      { day: 'Sun', isToday: true, isFuture: false, isCompleted: false, isMissed: false }
                    ]).map((dayData, index) => (
                      <div key={index} style={{ textAlign: 'center' }}>
                        <div style={{ 
                          fontSize: 10, 
                          color: dayData.isToday ? TEAL_ACCENT : '#9CA3AF', 
                          marginBottom: 6,
                          fontWeight: dayData.isToday ? 600 : 400
                        }}>
                          {dayData.day}
                        </div>
                        <div style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: dayData.isCompleted 
                            ? TEAL_ACCENT 
                            : dayData.isMissed 
                              ? MISSED_COLOR 
                              : dayData.isFuture 
                                ? '#F3F4F6' 
                                : 'transparent',
                          border: `2px solid ${
                            dayData.isCompleted 
                              ? TEAL_ACCENT 
                              : dayData.isMissed 
                                ? MISSED_COLOR 
                                : dayData.isToday 
                                  ? TEAL_ACCENT 
                                  : dayData.isFuture 
                                    ? '#E5E7EB' 
                                    : '#E5E7EB'
                          }`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s',
                          opacity: dayData.isFuture ? 0.5 : 1
                        }}>
                          {dayData.isCompleted && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                          {dayData.isMissed && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3">
                              <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                          )}
                          {dayData.isFuture && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                            </svg>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Legend */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 16, 
                  marginBottom: 16,
                  fontSize: 11,
                  color: '#6B7280'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: TEAL_ACCENT }} />
                    <span>Logged</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: MISSED_COLOR }} />
                    <span>Missed</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#F3F4F6', border: '1px solid #E5E7EB' }} />
                    <span>Upcoming</span>
                  </div>
                </div>

                {/* Streak Stats */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: 12 
                }}>
                  <div style={{
                    background: '#F9FAFB',
                    borderRadius: 10,
                    padding: '14px 12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: TEAL_ACCENT }}>
                      {weekStreakData?.current_streak || user.streak_count}
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                      Current Streak
                    </div>
                  </div>
                  <div style={{
                    background: '#F9FAFB',
                    borderRadius: 10,
                    padding: '14px 12px',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: ORANGE_ACCENT }}>
                      {weekStreakData?.longest_streak || user.longest_streak || user.streak_count}
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>
                      Longest Streak
                    </div>
                  </div>
                </div>

                {/* Motivational Footer */}
                <div style={{
                  marginTop: 16,
                  padding: '12px 16px',
                  background: TEAL_LIGHT,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10
                }}>
                  <TrendingUp size={18} color={TEAL_ACCENT} strokeWidth={1.5} />
                  <span style={{ fontSize: 13, color: '#374151' }}>
                    Keep it up! Log activity daily to grow your streak.
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ═══ LEADERBOARD SECTION ═══ */}
          <div className="reveal" style={{
            position: 'relative', overflow: 'hidden', borderRadius: 22,
            background: 'rgba(77, 182, 172, 0.05)', padding: 12, marginBottom: 16,
          }}>
            <div style={{ position: 'absolute', top: -50, left: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(77, 182, 172, 0.12)', animation: 'blobBreathe 10s ease-in-out infinite', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(231, 111, 81, 0.10)', animation: 'blobBreatheSlow 8s ease-in-out infinite', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
              <OrgLeaderboard currentUserId={user?.id} initialVisible={3} />
            </div>
          </div>

          {/* ═══ ACHIEVEMENTS SECTION ═══ */}
          <div className="reveal" style={{
            position: 'relative', overflow: 'hidden', borderRadius: 22,
            background: 'rgba(242, 140, 40, 0.05)', padding: 12, marginBottom: 32,
          }}>
            <div style={{ position: 'absolute', bottom: -50, right: -40, width: 190, height: 190, borderRadius: '50%', background: 'rgba(242, 140, 40, 0.12)', animation: 'blobBreathe 8s ease-in-out infinite', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', top: -30, left: -20, width: 110, height: 110, borderRadius: '50%', background: 'rgba(76, 175, 142, 0.10)', animation: 'blobBreatheSlow 9s ease-in-out infinite', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1 }}>
          {/* Achievements - with View All */}
          <div style={{ marginBottom: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', margin: 0 }}>Achievements</h3>
              {scoreData.achievements.length > 4 && (
                <button onClick={() => setShowAllAchievements(!showAllAchievements)} style={{
                  padding: '6px 12px', fontSize: 12, fontWeight: 500, color: TEAL_ACCENT,
                  background: 'transparent', border: `1px solid ${TEAL_ACCENT}`,
                  borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
                }}>
                  {showAllAchievements ? 'Show Less' : 'View All'}
                  <ChevronDown size={14} style={{ transform: showAllAchievements ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                </button>
              )}
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {(showAllAchievements ? scoreData.achievements : scoreData.achievements.slice(0, 4)).map((achievement) => (
                <div key={achievement.id} className="glow-card-impact glow-orange-i" style={{
                  background: '#FFFFFF', borderRadius: 12, padding: 12,
                  textAlign: 'center',
                  display: 'flex', flexDirection: 'column', alignItems: 'center'
                }}>
                  <div style={{ marginBottom: 6 }}>
                    {getBadgeIcon(achievement.badge_type || 'default', 20)}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: '#374151', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                    {achievement.title}
                  </div>
                </div>
              ))}
              
              {showAllAchievements && [...Array(Math.max(0, 8 - scoreData.achievements.length))].map((_, i) => (
                <div key={`locked-${i}`} style={{
                  background: '#F9FAFB', borderRadius: 12, padding: 12,
                  textAlign: 'center', border: '1px dashed #D1D5DB', opacity: 0.6,
                  display: 'flex', flexDirection: 'column', alignItems: 'center'
                }}>
                  <div style={{ 
                    width: 36, height: 36, borderRadius: '10px', 
                    background: '#E5E7EB', marginBottom: 6,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Award size={18} color="#9CA3AF" />
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 500, color: '#9CA3AF' }}>Locked</div>
                </div>
              ))}
            </div>
            
            {!showAllAchievements && scoreData.achievements.length > 4 && (
              <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: '#6B7280' }}>
                +{scoreData.achievements.length - 4} more achievements
              </div>
            )}
          </div>
          </div>
          </div>

        </div>
      </div>
      <BottomNav active="impact" />
      <style>{`
        @keyframes blobBreathe {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.18) rotate(10deg); }
        }
        @keyframes blobBreatheSlow {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.12) rotate(-8deg); }
        }
        .glow-card-impact {
          transition: transform 0.2s cubic-bezier(.22,1,.36,1), box-shadow 0.2s cubic-bezier(.22,1,.36,1), border-color 0.2s cubic-bezier(.22,1,.36,1);
        }
        .glow-teal-i {
          border: 1.5px solid rgba(77, 182, 172, 0.30) !important;
          box-shadow: 0 0 0 3px rgba(77, 182, 172, 0.07), 0 2px 12px rgba(77, 182, 172, 0.10);
        }
        .glow-teal-i:hover {
          transform: translateY(-3px);
          border-color: rgba(77, 182, 172, 0.45) !important;
          box-shadow: 0 0 0 4px rgba(77, 182, 172, 0.10), 0 4px 20px rgba(77, 182, 172, 0.18);
        }
        .glow-green-i {
          border: 1.5px solid rgba(76, 175, 142, 0.28) !important;
          box-shadow: 0 0 0 3px rgba(76, 175, 142, 0.06), 0 2px 12px rgba(76, 175, 142, 0.10);
        }
        .glow-green-i:hover {
          transform: translateY(-3px);
          border-color: rgba(76, 175, 142, 0.42) !important;
          box-shadow: 0 0 0 4px rgba(76, 175, 142, 0.10), 0 4px 20px rgba(76, 175, 142, 0.18);
        }
        .glow-orange-i {
          border: 1.5px solid rgba(242, 140, 40, 0.30) !important;
          box-shadow: 0 0 0 3px rgba(242, 140, 40, 0.07), 0 2px 12px rgba(242, 140, 40, 0.10);
        }
        .glow-orange-i:hover {
          transform: translateY(-3px);
          border-color: rgba(242, 140, 40, 0.45) !important;
          box-shadow: 0 0 0 4px rgba(242, 140, 40, 0.10), 0 4px 20px rgba(242, 140, 40, 0.18);
        }
        .glow-red-orange-i {
          border: 1.5px solid rgba(231, 111, 81, 0.30) !important;
          box-shadow: 0 0 0 3px rgba(231, 111, 81, 0.07), 0 2px 12px rgba(231, 111, 81, 0.11);
        }
        .glow-red-orange-i:hover {
          transform: translateY(-3px);
          border-color: rgba(231, 111, 81, 0.45) !important;
          box-shadow: 0 0 0 4px rgba(231, 111, 81, 0.10), 0 4px 20px rgba(231, 111, 81, 0.18);
        }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

// ME Page - Redesigned
export const ProfilePage = ({ TopHeader, BottomNav, useAuth }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myChallenges, setMyChallenges] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [myActivity, setMyActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('mymad'); // 'mymad', 'myposts', 'myactivity'
  const [madSubFilter, setMadSubFilter] = useState('active');
  const [showAllMAD, setShowAllMAD] = useState(false);
  const tabBarRef = useRef(null);
  
  // Weekly glance state
  const [weekDays, setWeekDays] = useState([]);
  const [weekEvents, setWeekEvents] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventSheet, setShowEventSheet] = useState(false);

  // Theme colors
  const TEAL = '#47b8a3';
  const TEAL_DARK = '#3a9e8c';
  const TEAL_LIGHT = '#EDF8F6';
  const ORANGE_LIGHT = '#FEF7ED';

  const revealRef = useScrollReveal([loading]);

  useEffect(() => {
    fetchData();
    initializeWeek();
  }, []);

  // Initialize current week
  const initializeWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      days.push({
        date: date,
        dayName: date.toLocaleDateString('en-US', { weekday: 'short' }),
        dayNum: date.getDate(),
        isToday: date.toDateString() === today.toDateString(),
        dateKey: date.toISOString().split('T')[0]
      });
    }
    setWeekDays(days);
    fetchWeekEvents(days[0].dateKey, days[6].dateKey);
  };

  // Fetch events for the week
  const fetchWeekEvents = async (startDate, endDate) => {
    try {
      const token = localStorage.getItem('token');
      const [challengesRes] = await Promise.all([
        axios.get(`${API}/challenges/my`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const events = {};
      const challenges = challengesRes.data || [];
      
      challenges.forEach(challenge => {
        if (challenge.sessions && Array.isArray(challenge.sessions)) {
          challenge.sessions.forEach(session => {
            const sessionDate = session.date?.split('T')[0];
            if (sessionDate) {
              if (!events[sessionDate]) events[sessionDate] = [];
              events[sessionDate].push({
                id: session.id || challenge.id,
                title: challenge.title,
                time: session.time || '09:00',
                type: 'mad_session',
                typeLabel: 'MAD Session',
                location: session.location || 'Online',
                challengeId: challenge.id
              });
            }
          });
        }
        
        if (challenge.start_date) {
          const startDateKey = challenge.start_date.split('T')[0];
          if (!events[startDateKey]) events[startDateKey] = [];
          if (startDateKey >= startDate && startDateKey <= endDate) {
            events[startDateKey].push({
              id: `start-${challenge.id}`,
              title: challenge.title,
              time: '09:00',
              type: 'mad_session',
              typeLabel: 'MAD Action Starts',
              location: challenge.location || 'Online',
              challengeId: challenge.id
            });
          }
        }
      });
      
      setWeekEvents(events);
    } catch (error) {
      console.error('Failed to fetch week events:', error);
    }
  };

  // Handle date tap
  const handleDateTap = (day) => {
    const dayEvents = weekEvents[day.dateKey] || [];
    if (dayEvents.length > 0) {
      setSelectedDate(day);
      setShowEventSheet(true);
    }
  };

  // Handle event item tap
  const handleEventTap = (event) => {
    setShowEventSheet(false);
    if (event.type === 'admin_event') {
      navigate(`/events/${event.id}`);
    } else if (event.type === 'mad_session') {
      navigate(`/challenges/${event.challengeId}`);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch all data in parallel
      const [challengesRes, postsRes, activityRes] = await Promise.all([
        axios.get(`${API}/challenges/my`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/posts/my`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/activity/my`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      
      setMyChallenges(challengesRes.data || []);
      setMyPosts(postsRes.data || []);
      setMyActivity(activityRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImpactBadge = (level) => {
    const badgeConfig = {
      'Bronze': { icon: Medal, color: '#CD7F32', bg: 'rgba(205, 127, 50, 0.15)' },
      'Silver': { icon: Medal, color: '#A8A8A8', bg: 'rgba(168, 168, 168, 0.15)' },
      'Gold': { icon: Medal, color: '#D4AF37', bg: 'rgba(212, 175, 55, 0.15)' },
      'Platinum': { icon: Gem, color: '#9D5CFF', bg: 'rgba(157, 92, 255, 0.15)' },
      'Hero': { icon: Trophy, color: '#F5A623', bg: 'rgba(245, 166, 35, 0.15)' }
    };
    const config = badgeConfig[level] || { icon: Star, color: TEAL, bg: TEAL_LIGHT };
    const IconComponent = config.icon;
    return <IconComponent size={14} color={config.color} strokeWidth={2} />;
  };

  // An action is expired when its end_date has passed.
  const isExpired = (c) => !!c?.end_date && new Date(c.end_date) < new Date();

  // Filter MAD challenges based on sub-filter
  const getFilteredChallenges = () => {
    const userId = user?.id;
    switch (madSubFilter) {
      case 'active':
        // Active = user is a participant, action is still running, and
        // not yet completed. Expired actions are surfaced under the
        // "Completed" or "Expired" buckets, not here.
        return myChallenges.filter(c =>
          (c.is_active || c.status === 'active' || c.status === 'in_progress' || c.status === 'joined') &&
          !isExpired(c) &&
          c.status !== 'completed' &&
          !(c.progress_percentage >= 100)
        );
      case 'created':
        return myChallenges.filter(c => c.created_by === userId);
      case 'completed':
        return myChallenges.filter(c =>
          c.status === 'completed' || c.progress_percentage >= 100 || isExpired(c)
        );
      default:
        return myChallenges;
    }
  };

  const filteredChallenges = getFilteredChallenges();
  const displayedChallenges = showAllMAD ? filteredChallenges : filteredChallenges.slice(0, 4);

  // Get activity icon based on type — SVG only, no emoji
  const getActivityIcon = (type) => {
    const iconProps = { size: 15, strokeWidth: 2 };
    switch (type) {
      case 'like': return <Heart {...iconProps} color="#EF4444" />;
      case 'comment': return <MessageSquare {...iconProps} color="#6366F1" />;
      case 'join': return <Target {...iconProps} color={TEAL} />;
      case 'progress': return <TrendingUp {...iconProps} color="#F59E0B" />;
      case 'complete': return <CheckCircle {...iconProps} color="#10B981" />;
      case 'post': return <FileText {...iconProps} color="#3B82F6" />;
      default: return <Zap {...iconProps} color="#8B5CF6" />;
    }
  };

  // Format activity text
  const formatActivityText = (activity) => {
    switch (activity.type) {
      case 'like': return `Liked a post`;
      case 'comment': return `Commented on a post`;
      case 'join': return `Joined "${activity.target_title || 'a MAD action'}"`;
      case 'progress': return `Logged progress for "${activity.target_title || 'a MAD action'}"`;
      case 'complete': return `Completed "${activity.target_title || 'a MAD action'}"`;
      case 'post': return `Shared a post`;
      default: return activity.description || 'Activity logged';
    }
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Get card glow class based on content type/category
  const getCardGlow = (type, category) => {
    if (type === 'mad') {
      const cat = (category || '').toLowerCase();
      if (cat.includes('climate') || cat.includes('environment') || cat.includes('green') || cat.includes('energy')) return 'glow-card glow-teal';
      if (cat.includes('team') || cat.includes('social') || cat.includes('community')) return 'glow-card glow-green';
      if (cat.includes('survey') || cat.includes('update') || cat.includes('announcement')) return 'glow-card glow-indigo';
      if (cat.includes('celebrat') || cat.includes('memory') || cat.includes('event')) return 'glow-card glow-red-orange';
      return 'glow-card glow-orange';
    }
    if (type === 'post') return 'glow-card glow-green';
    if (type === 'activity') return 'glow-card glow-indigo';
    return 'glow-card glow-orange';
  };

  // Tab configuration — SVG line icons only
  const tabs = [
    { id: 'mymad', label: 'Actions', Icon: Clock },
    { id: 'myposts', label: 'Posts', Icon: FileText },
    { id: 'myactivity', label: 'Activity', Icon: Activity }
  ];

  // Scroll tab bar into view + switch tab when stat is tapped
  const handleStatTap = (tabId) => {
    setActiveTab(tabId);
    setShowAllMAD(false);
    tabBarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Week date range label
  const weekRangeLabel = weekDays.length >= 7
    ? `${weekDays[0].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${weekDays[6].date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
    : '';

  return (
    <div className="page-container" data-testid="profile-page" ref={revealRef}>
      <TopHeader title="" showBack={false} />
      <div className="content-wrapper" style={{ background: '#F8FAFB' }}>
        <div className="content-section reveal-stagger" style={{ padding: '0 16px' }}>
          
          {/* ═══ PROFILE CARD SECTION ═══ */}
          <div className="reveal" style={{
            position: 'relative', overflow: 'hidden', borderRadius: 24,
            background: 'rgba(77, 182, 172, 0.06)', padding: 12, marginBottom: 16,
          }}>
            {/* Blobs */}
            <div style={{ position: 'absolute', top: -60, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(77, 182, 172, 0.14)', animation: 'blobBreathe 9s ease-in-out infinite', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -40, left: -30, width: 130, height: 130, borderRadius: '50%', background: 'rgba(76, 175, 142, 0.10)', animation: 'blobBreatheSlow 10s ease-in-out infinite', pointerEvents: 'none' }} />
            
            <div data-testid="profile-card" style={{
              position: 'relative', zIndex: 1,
              background: `linear-gradient(135deg, ${TEAL} 0%, ${TEAL_DARK} 100%)`,
              borderRadius: 20,
              padding: '24px 20px 20px',
              color: 'white',
            }}>
            {/* Top row: avatar + name + badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
              <div data-testid="profile-avatar" style={{
                width: 58, height: 58, borderRadius: '50%',
                background: 'rgba(255,255,255,0.18)',
                border: '2.5px solid rgba(255,255,255,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 24, fontWeight: 700, letterSpacing: '-0.5px', flexShrink: 0,
              }}>
                {user.username?.charAt(0).toUpperCase()}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div data-testid="profile-username" style={{ fontSize: 19, fontWeight: 700, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
                  {user.username}
                </div>
                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                  {user.role === 'HR_ADMIN' ? 'HR Admin' : 'Team Member'}
                </div>
              </div>

              {/* Badge pushed to the right */}
              <button
                data-testid="profile-level-badge"
                onClick={() => navigate('/impact?showLevels=true')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '4px 10px', flexShrink: 0,
                  background: 'rgba(255,255,255,0.18)', borderRadius: 20,
                  border: '1px solid rgba(255,255,255,0.25)',
                  fontSize: 11, fontWeight: 600, color: 'white', cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                {getImpactBadge(user.impact_level)}
                <span>{user.impact_level || 'Bronze'} Level</span>
                <ChevronRight size={12} style={{ opacity: 0.7 }} />
              </button>
            </div>
            {/* Stats row — tappable, scrolls to tab */}
            <div style={{
              display: 'flex', justifyContent: 'space-around',
              paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.18)',
            }}>
              {[
                { label: 'Stars', value: user.points?.toLocaleString() || '0', tab: null },
                { label: 'Actions', value: myChallenges.length, tab: 'mymad' },
                { label: 'Posts', value: myPosts.length, tab: 'myposts' },
              ].map((stat, i, arr) => (
                <React.Fragment key={stat.label}>
                  <button
                    data-testid={`profile-stat-${stat.label.toLowerCase()}`}
                    onClick={() => stat.tab && handleStatTap(stat.tab)}
                    style={{
                      background: 'none', border: 'none', cursor: stat.tab ? 'pointer' : 'default',
                      textAlign: 'center', padding: '0 8px', color: 'white',
                    }}
                  >
                    <div style={{ fontSize: 21, fontWeight: 700, letterSpacing: '-0.5px' }}>{stat.value}</div>
                    <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>{stat.label}</div>
                  </button>
                  {i < arr.length - 1 && (
                    <div style={{ width: 1, height: 36, background: 'rgba(255,255,255,0.18)', alignSelf: 'center' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          </div>

          {/* ═══ THIS WEEK STRIP ═══ */}
          <div className="reveal" style={{
            position: 'relative', overflow: 'hidden', borderRadius: 20,
            background: 'rgba(242, 140, 40, 0.06)', padding: '16px 12px', marginBottom: 16,
          }}>
            {/* Blobs */}
            <div style={{ position: 'absolute', top: -50, right: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(242, 140, 40, 0.13)', animation: 'blobBreathe 8s ease-in-out infinite', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -30, left: -20, width: 110, height: 110, borderRadius: '50%', background: 'rgba(77, 182, 172, 0.10)', animation: 'blobBreatheSlow 9s ease-in-out infinite', pointerEvents: 'none' }} />

            <div data-testid="this-week-strip" style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '0 0 10px 0' }}>
              <h3 style={{ fontSize: 13, fontWeight: 700, color: '#1F2937', letterSpacing: '-0.2px', margin: 0 }}>
                This Week
              </h3>
              <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 500 }}>{weekRangeLabel}</span>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              background: '#FFFFFF', borderRadius: 14,
              padding: '10px 6px', border: '1px solid #E5E7EB',
            }}>
              {weekDays.map((day) => {
                const hasEvents = weekEvents[day.dateKey]?.length > 0;
                return (
                  <button key={day.dateKey} data-testid={`week-day-${day.dateKey}`}
                    onClick={() => handleDateTap(day)}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center',
                      padding: '6px 0', background: 'transparent',
                      borderRadius: 10, border: 'none',
                      cursor: hasEvents ? 'pointer' : 'default', minWidth: 40, position: 'relative',
                    }}>
                    <span style={{ fontSize: 10, fontWeight: 500, color: day.isToday ? TEAL : '#9CA3AF', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {day.dayName.slice(0, 2)}
                    </span>
                    <span style={{
                      width: 30, height: 30, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 600,
                      color: day.isToday ? '#FFFFFF' : '#374151',
                      background: day.isToday ? TEAL : 'transparent',
                    }}>
                      {day.dayNum}
                    </span>
                    {hasEvents && (
                      <div style={{
                        width: 4, height: 4, borderRadius: '50%',
                        background: day.isToday ? TEAL : '#CBD5E1', marginTop: 3,
                      }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          </div>

          {/* Event Bottom Sheet */}
          {showEventSheet && selectedDate && (
            <>
              <div 
                onClick={() => setShowEventSheet(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0, 0, 0, 0.25)',
                  zIndex: 1000
                }}
              />
              <div style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                background: '#FFFFFF',
                borderRadius: '20px 20px 0 0',
                maxHeight: '45vh',
                zIndex: 1001,
                animation: 'slideUp 0.3s ease',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
                  <div style={{ width: 40, height: 4, borderRadius: 2, background: '#E5E7EB' }} />
                </div>
                <div style={{ padding: '0 20px 16px', borderBottom: '1px solid #F3F4F6' }}>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1F2937' }}>
                    {selectedDate.date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </div>
                  <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>
                    {weekEvents[selectedDate.dateKey]?.length || 0} scheduled item{weekEvents[selectedDate.dateKey]?.length !== 1 ? 's' : ''}
                  </div>
                </div>
                <div style={{ padding: '12px 20px 24px', overflowY: 'auto', maxHeight: 'calc(45vh - 100px)' }}>
                  {(weekEvents[selectedDate.dateKey] || []).map((event, idx) => (
                    <button
                      key={event.id || idx}
                      onClick={() => handleEventTap(event)}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 12,
                        width: '100%',
                        padding: '14px',
                        background: '#F9FAFB',
                        border: '1px solid #E5E7EB',
                        borderRadius: 12,
                        marginBottom: 10,
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: TEAL, minWidth: 50 }}>
                        {event.time}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937', marginBottom: 4 }}>
                          {event.title}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: event.type === 'admin_event' ? '#7C3AED' : TEAL,
                            background: event.type === 'admin_event' ? 'rgba(124, 58, 237, 0.1)' : TEAL_LIGHT,
                            padding: '2px 8px',
                            borderRadius: 4
                          }}>
                            {event.typeLabel}
                          </span>
                          {event.location && (
                            <span style={{ fontSize: 11, color: '#6B7280' }}>{event.location}</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight size={18} color="#9CA3AF" />
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ═══ TAB BAR SECTION ═══ */}
          <div className="reveal" style={{
            position: 'relative', overflow: 'hidden', borderRadius: 22,
            background: 'rgba(76, 175, 142, 0.05)', padding: 10, marginBottom: 16,
          }}>
            {/* Blobs */}
            <div style={{ position: 'absolute', top: -60, left: -50, width: 210, height: 210, borderRadius: '50%', background: 'rgba(76, 175, 142, 0.12)', animation: 'blobBreathe 10s ease-in-out infinite', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: -50, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(242, 140, 40, 0.10)', animation: 'blobBreatheSlow 8s ease-in-out infinite', pointerEvents: 'none' }} />

            <div ref={tabBarRef} data-testid="profile-tab-bar" style={{
              position: 'relative', zIndex: 1,
              background: '#FFFFFF', borderRadius: 16,
              border: '1px solid #E5E7EB', overflow: 'hidden',
            }}>
            {/* Tab header */}
            <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB' }}>
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                const TabIcon = tab.Icon;
                return (
                  <button key={tab.id} data-testid={`tab-${tab.id}`}
                    className="tab-animate"
                    onClick={() => { setActiveTab(tab.id); setShowAllMAD(false); }}
                    style={{
                      flex: 1, padding: '13px 8px', border: 'none',
                      background: 'transparent', cursor: 'pointer', position: 'relative',
                    }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      fontSize: 13, fontWeight: isActive ? 650 : 500,
                      color: isActive ? TEAL : '#94A3B8',
                      transition: 'color 0.2s',
                    }}>
                      <TabIcon size={16} strokeWidth={isActive ? 2.2 : 1.8} />
                      <span>{tab.label}</span>
                    </div>
                    {isActive && (
                      <div style={{
                        position: 'absolute', bottom: 0, left: '15%', right: '15%',
                        height: 2.5, background: TEAL, borderRadius: '3px 3px 0 0',
                      }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div style={{ minHeight: 200 }}>
              
              {/* ========== TAB 1: MY MAD ========== */}
              {activeTab === 'mymad' && (
                <div style={{ padding: 16 }}>
                  {/* Sub-filter toggle */}
                  <div style={{
                    display: 'flex',
                    gap: 8,
                    marginBottom: 16,
                    background: '#F3F4F6',
                    padding: 4,
                    borderRadius: 10
                  }}>
                    {[
                      { id: 'active', label: 'Active' },
                      { id: 'created', label: 'Created' },
                      { id: 'completed', label: 'Completed' }
                    ].map((filter) => (
                      <button
                        key={filter.id}
                        onClick={() => {
                          setMadSubFilter(filter.id);
                          setShowAllMAD(false);
                        }}
                        className="tab-animate"
                        data-testid={`mad-filter-${filter.id}`}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          fontSize: 12,
                          fontWeight: madSubFilter === filter.id ? 600 : 500,
                          color: madSubFilter === filter.id ? '#FFFFFF' : '#6B7280',
                          background: madSubFilter === filter.id ? TEAL : 'transparent',
                          border: 'none',
                          borderRadius: 8,
                          cursor: 'pointer',
                        }}
                      >
                        {filter.label}
                      </button>
                    ))}
                  </div>

                  {/* MAD Cards List */}
                  {filteredChallenges.length > 0 ? (
                    <>
                      <div style={{ display: 'grid', gap: 10 }}>
                        {displayedChallenges.map((challenge) => (
                          <div 
                            key={challenge.id} 
                            data-testid={`mad-card-${challenge.id}`}
                            className={getCardGlow('mad', challenge.category)}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              padding: '14px 16px',
                              background: '#FAFBFC',
                              borderRadius: 12,
                              width: '100%',
                              cursor: 'pointer',
                            }}
                          >
                            {/* Progress Circle - Click to Log Activity */}
                            <button
                              onClick={() => navigate(`/log-activity?challenge=${challenge.id}`)}
                              data-testid={`log-activity-${challenge.id}`}
                              style={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                background: `conic-gradient(${TEAL} ${(challenge.progress_percentage || 0) * 3.6}deg, #E5E7EB 0deg)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                cursor: 'pointer',
                                border: 'none',
                                padding: 0
                              }}
                            >
                              <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 11,
                                fontWeight: 700,
                                color: '#374151'
                              }}>
                                {Math.round(challenge.progress_percentage || 0)}%
                              </div>
                            </button>
                            
                            {/* Title - Click to Log Activity */}
                            <button
                              onClick={() => navigate(`/log-activity?challenge=${challenge.id}`)}
                              style={{
                                flex: 1,
                                minWidth: 0,
                                textAlign: 'left',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0
                              }}
                            >
                              <div style={{ 
                                fontSize: 14, 
                                fontWeight: 600, 
                                color: '#1F2937',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {challenge.title}
                              </div>
                              <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                                {challenge.category || 'Daily'}
                              </div>
                            </button>
                            
                            {/* Details Button */}
                            <button
                              onClick={() => navigate(`/challenges/${challenge.id}`)}
                              data-testid={`view-details-${challenge.id}`}
                              style={{
                                padding: '6px 12px',
                                fontSize: 11,
                                fontWeight: 600,
                                color: TEAL,
                                background: TEAL_LIGHT,
                                border: 'none',
                                borderRadius: 6,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Details
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      {/* View All Button */}
                      {filteredChallenges.length > 4 && (
                        <button
                          onClick={() => setShowAllMAD(!showAllMAD)}
                          data-testid="mad-view-all"
                          style={{
                            width: '100%',
                            padding: '12px',
                            marginTop: 12,
                            fontSize: 13,
                            fontWeight: 600,
                            color: TEAL,
                            background: TEAL_LIGHT,
                            border: 'none',
                            borderRadius: 10,
                            cursor: 'pointer'
                          }}
                        >
                          {showAllMAD ? 'Show Less' : `View All (${filteredChallenges.length})`}
                        </button>
                      )}
                    </>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '32px 20px',
                      color: '#6B7280'
                    }}>
                      <Target size={32} color="#CBD5E1" strokeWidth={1.5} style={{ marginBottom: 12 }} />
                      <div style={{ fontSize: 14, fontWeight: 500 }}>
                        {madSubFilter === 'active' && "No active actions"}
                        {madSubFilter === 'created' && "You haven't created any actions yet"}
                        {madSubFilter === 'completed' && "No completed actions yet"}
                      </div>
                      <button
                        onClick={() => navigate('/')}
                        style={{
                          marginTop: 16,
                          padding: '10px 20px',
                          background: TEAL,
                          color: 'white',
                          border: 'none',
                          borderRadius: 10,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Browse Actions
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ========== TAB 2: MY POSTS ========== */}
              {activeTab === 'myposts' && (
                <div style={{ padding: 16 }}>
                  {myPosts.length > 0 ? (
                    <div style={{ display: 'grid', gap: 12 }}>
                      {myPosts.slice(0, showAllMAD ? undefined : 4).map((post) => (
                        <div 
                          key={post.id}
                          data-testid={`post-card-${post.id}`}
                          className="glow-card glow-green"
                          style={{
                            background: '#FAFBFC',
                            borderRadius: 12,
                            padding: 14,
                            position: 'relative',
                            cursor: 'pointer',
                          }}
                        >
                          {/* Post Header */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                            <div style={{ fontSize: 12, color: '#6B7280' }}>
                              {formatTimeAgo(post.created_at)}
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                              {/* Edit is hidden for system-generated posts
                                  (admin announcements, auto-generated notices etc).
                                  Backend also rejects edits on these. */}
                              {!(post.is_system_post || post.type === 'admin_announcement') && (
                                <button
                                  onClick={() => navigate(`/edit-post/${post.id}`)}
                                  data-testid={`profile-post-edit-${post.id}`}
                                  style={{
                                    padding: '4px 10px',
                                    fontSize: 11,
                                    fontWeight: 500,
                                    color: TEAL,
                                    background: TEAL_LIGHT,
                                    border: 'none',
                                    borderRadius: 6,
                                    cursor: 'pointer'
                                  }}
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* Post Content */}
                          <div style={{ 
                            fontSize: 14, 
                            color: '#374151', 
                            lineHeight: 1.5,
                            marginBottom: post.image ? 10 : 0
                          }}>
                            {post.content?.length > 120 ? `${post.content.slice(0, 120)}...` : post.content}
                          </div>
                          
                          {/* Post Image */}
                          {post.image && (
                            <img 
                              src={post.image} 
                              alt="" 
                              style={{
                                width: '100%',
                                height: 120,
                                objectFit: 'cover',
                                borderRadius: 8
                              }}
                            />
                          )}
                          
                          {/* Hashtags */}
                          {post.hashtags && post.hashtags.length > 0 && (
                            <div style={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: 6, 
                              marginTop: 10 
                            }}>
                              {post.hashtags.slice(0, 3).map((tag, idx) => (
                                <span key={idx} style={{
                                  fontSize: 11,
                                  color: TEAL,
                                  background: TEAL_LIGHT,
                                  padding: '2px 8px',
                                  borderRadius: 4
                                }}>
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          {/* Engagement Stats */}
                          <div style={{ 
                            display: 'flex', 
                            gap: 16, 
                            marginTop: 12,
                            paddingTop: 10,
                            borderTop: '1px solid #E5E7EB',
                            fontSize: 12,
                            color: '#6B7280'
                          }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Heart size={13} color="#EF4444" strokeWidth={2} /> {post.likes_count || 0}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MessageSquare size={13} color="#6366F1" strokeWidth={2} /> {post.comments_count || 0}</span>
                          </div>
                        </div>
                      ))}
                      
                      {myPosts.length > 4 && (
                        <button
                          onClick={() => setShowAllMAD(!showAllMAD)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            fontSize: 13,
                            fontWeight: 600,
                            color: TEAL,
                            background: TEAL_LIGHT,
                            border: 'none',
                            borderRadius: 10,
                            cursor: 'pointer'
                          }}
                        >
                          {showAllMAD ? 'Show Less' : `View All (${myPosts.length})`}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '32px 20px',
                      color: '#6B7280'
                    }}>
                      <FileText size={32} color="#CBD5E1" strokeWidth={1.5} style={{ marginBottom: 12 }} />
                      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                        You haven't shared any posts yet.
                      </div>
                      <div style={{ fontSize: 13, color: '#9CA3AF' }}>
                        Share your wellness journey with your colleagues
                      </div>
                      <button
                        onClick={() => navigate('/create-post')}
                        style={{
                          marginTop: 16,
                          padding: '10px 20px',
                          background: TEAL,
                          color: 'white',
                          border: 'none',
                          borderRadius: 10,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        Create Your First Post
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ========== TAB 3: MY ACTIVITY ========== */}
              {activeTab === 'myactivity' && (
                <div style={{ padding: 16 }}>
                  {myActivity.length > 0 ? (
                    <div style={{ display: 'grid', gap: 0 }}>
                      {myActivity.slice(0, showAllMAD ? undefined : 10).map((activity, idx) => (
                        <div 
                          key={activity.id || idx}
                          data-testid={`activity-item-${idx}`}
                          className="glow-card glow-indigo"
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 12,
                            padding: '12px 14px',
                            borderRadius: 10,
                            background: '#FAFBFC',
                            marginBottom: 8,
                          }}
                        >
                          {/* Activity Icon */}
                          <div style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: '#F3F4F6',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                            flexShrink: 0
                          }}>
                            {getActivityIcon(activity.type)}
                          </div>
                          
                          {/* Activity Content */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              fontSize: 13, 
                              color: '#374151',
                              lineHeight: 1.4
                            }}>
                              {formatActivityText(activity)}
                            </div>
                            <div style={{ 
                              fontSize: 11, 
                              color: '#9CA3AF',
                              marginTop: 2
                            }}>
                              {formatTimeAgo(activity.created_at)}
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {myActivity.length > 10 && (
                        <button
                          onClick={() => setShowAllMAD(!showAllMAD)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            marginTop: 12,
                            fontSize: 13,
                            fontWeight: 600,
                            color: TEAL,
                            background: TEAL_LIGHT,
                            border: 'none',
                            borderRadius: 10,
                            cursor: 'pointer'
                          }}
                        >
                          {showAllMAD ? 'Show Less' : `View All (${myActivity.length})`}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '32px 20px',
                      color: '#6B7280'
                    }}>
                      <Activity size={32} color="#CBD5E1" strokeWidth={1.5} style={{ marginBottom: 12 }} />
                      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
                        Your activity will appear here.
                      </div>
                      <div style={{ fontSize: 13, color: '#9CA3AF' }}>
                        Join actions, like posts, and log your progress
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          </div>
          {/* ═══ END TAB BAR SECTION ═══ */}

          {/* ═══ QUICK-LINK TILES SECTION ═══ */}
          <div className="reveal" style={{
            position: 'relative', overflow: 'hidden', borderRadius: 22,
            background: 'rgba(231, 111, 81, 0.05)', padding: 10, marginBottom: 20,
          }}>
            {/* Blobs */}
            <div style={{ position: 'absolute', bottom: -50, right: -40, width: 190, height: 190, borderRadius: '50%', background: 'rgba(231, 111, 81, 0.12)', animation: 'blobBreathe 9s ease-in-out infinite', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', top: -30, left: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(76, 175, 142, 0.10)', animation: 'blobBreatheSlow 10s ease-in-out infinite', pointerEvents: 'none' }} />

            <div style={{ position: 'relative', zIndex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <button
              onClick={() => navigate('/rewards')}
              data-testid="redeem-stars-btn"
              className="glow-card glow-orange btn-pulse"
              style={{
                background: ORANGE_LIGHT,
                borderRadius: 16,
                padding: '20px 16px',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <Gift size={24} color="#F59E0B" strokeWidth={1.5} />
                <ChevronRight size={18} color="#F59E0B" />
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#92400E', marginBottom: 2 }}>
                {user.points?.toLocaleString() || 0}
              </div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#B45309' }}>
                Redeem Stars
              </div>
            </button>

            <button
              onClick={() => navigate('/impact')}
              data-testid="impact-dashboard-btn"
              className="glow-card glow-teal"
              style={{
                background: TEAL_LIGHT,
                borderRadius: 16,
                padding: '20px 16px',
                cursor: 'pointer',
                textAlign: 'left'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <BarChart3 size={24} color={TEAL} strokeWidth={1.5} />
                <ChevronRight size={18} color={TEAL} />
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#115E59', marginBottom: 2 }}>
                Track Your
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#115E59' }}>
                Impact Dashboard
              </div>
            </button>
          </div>
          </div>

        </div>
      </div>
      <BottomNav active="profile" />
      
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes blobBreathe {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.18) rotate(10deg); }
        }
        @keyframes blobBreatheSlow {
          0%, 100% { transform: scale(1) rotate(0deg); }
          50% { transform: scale(1.12) rotate(-8deg); }
        }
        /* ── Card glow system ── */
        .glow-card {
          transition: transform 0.2s cubic-bezier(.22,1,.36,1), box-shadow 0.2s cubic-bezier(.22,1,.36,1), border-color 0.2s cubic-bezier(.22,1,.36,1);
        }
        /* Orange — MAD Action cards */
        .glow-orange {
          border: 1.5px solid rgba(242, 140, 40, 0.30);
          box-shadow: 0 0 0 3px rgba(242, 140, 40, 0.07), 0 2px 12px rgba(242, 140, 40, 0.10);
        }
        .glow-orange:hover {
          transform: translateY(-3px);
          border-color: rgba(242, 140, 40, 0.45);
          box-shadow: 0 0 0 4px rgba(242, 140, 40, 0.10), 0 4px 20px rgba(242, 140, 40, 0.18);
        }
        /* Teal — Climate category */
        .glow-teal {
          border: 1.5px solid rgba(77, 182, 172, 0.30);
          box-shadow: 0 0 0 3px rgba(77, 182, 172, 0.07), 0 2px 12px rgba(77, 182, 172, 0.10);
        }
        .glow-teal:hover {
          transform: translateY(-3px);
          border-color: rgba(77, 182, 172, 0.45);
          box-shadow: 0 0 0 4px rgba(77, 182, 172, 0.10), 0 4px 20px rgba(77, 182, 172, 0.18);
        }
        /* Green — Team/social cards */
        .glow-green {
          border: 1.5px solid rgba(76, 175, 142, 0.28);
          box-shadow: 0 0 0 3px rgba(76, 175, 142, 0.06), 0 2px 12px rgba(76, 175, 142, 0.10);
        }
        .glow-green:hover {
          transform: translateY(-3px);
          border-color: rgba(76, 175, 142, 0.42);
          box-shadow: 0 0 0 4px rgba(76, 175, 142, 0.10), 0 4px 20px rgba(76, 175, 142, 0.18);
        }
        /* Indigo — Survey/update cards */
        .glow-indigo {
          border: 1.5px solid rgba(99, 102, 241, 0.28);
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.06), 0 2px 12px rgba(99, 102, 241, 0.11);
        }
        .glow-indigo:hover {
          transform: translateY(-3px);
          border-color: rgba(99, 102, 241, 0.42);
          box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.10), 0 4px 20px rgba(99, 102, 241, 0.18);
        }
        /* Red-orange — Memory/celebration */
        .glow-red-orange {
          border: 1.5px solid rgba(231, 111, 81, 0.30);
          box-shadow: 0 0 0 3px rgba(231, 111, 81, 0.07), 0 2px 12px rgba(231, 111, 81, 0.11);
        }
        .glow-red-orange:hover {
          transform: translateY(-3px);
          border-color: rgba(231, 111, 81, 0.45);
          box-shadow: 0 0 0 4px rgba(231, 111, 81, 0.10), 0 4px 20px rgba(231, 111, 81, 0.18);
        }
        /* Quick-link tile glow */
        .glow-tile {
          transition: transform 0.2s cubic-bezier(.22,1,.36,1), box-shadow 0.2s cubic-bezier(.22,1,.36,1);
        }
        .glow-tile:hover {
          transform: translateY(-3px);
        }
      `}</style>
    </div>
  );
};
